import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenHash, setTokenHash] = useState(null);
  const [email, setEmail] = useState(null);
  const [type, setType] = useState(null);
  const [sessionValid, setSessionValid] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract token and type from query params on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenHashParam = params.get("tokenHash");
	console.log("Token from URL:", tokenHashParam);
    const typeParam = params.get("type");
	console.log("Type from URL:", typeParam);
	const email = params.get("email");
	 console.log("Email from URL:", email);
    setTokenHash(tokenHashParam);
    setType(typeParam);
	setEmail(email);
  }, [location.search]);

  // Explicitly verify the OTP token to create a session
  useEffect(() => {
    async function verifyRecoveryToken() {
      if (type === "recovery" && tokenHash && email) {
        setError("");
        setLoading(true);
        const { data, error } = await supabase.auth.verifyOtp({
          type: "recovery",
          token_hash: tokenHash,
        });
		console.log("Recovery token verification data:", data);
		console.log("Recovery token verification error:", error);
        setLoading(false);
        if (error) {
          setError("Invalid or expired reset link. Please request a new one.");
          setSessionValid(false);
        } else {
          setSessionValid(true);
        }
      }
    }
    verifyRecoveryToken();
  }, [type, tokenHash, email]);

  // Handle password submission
  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!password || !confirm) {
      setError("Please enter and confirm your new password.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!sessionValid) {
      setError("You do not have a valid reset session.");
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.auth.updateUser({ password });
	console.log("Password update data:", data);
	console.log("Password update error:", error);
    setLoading(false);

    if (error) setError(error.message);
    else {
      setSuccess("Your password has been updated! Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);
    }
  }

  // Error screens and loading states
  if (!tokenHash || type !== "recovery" || !email) {
    return (
      <div className="max-w-sm mx-auto mt-12 p-6 bg-white rounded-2xl shadow">
        <h2 className="text-xl font-bold mb-4">Invalid Link</h2>
        <p className="text-sm">This password reset link is invalid or expired.</p>
      </div>
    );
  }

  if (loading && !sessionValid) {
    return (
      <div className="max-w-sm mx-auto mt-24 text-center">
        <div className="text-lg font-bold">Verifying link...</div>
      </div>
    );
  }

  if (!sessionValid && !loading) {
    return (
      <div className="max-w-sm mx-auto mt-12 p-6 bg-white rounded-2xl shadow">
        <h2 className="text-xl font-bold mb-4">Invalid or Expired Link</h2>
        <p className="text-sm">{error || "This password reset link is invalid or expired."}</p>
        <a href="/forgot-password" className="block mt-4 text-center underline text-blue-600">
          Request a new link
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-sm mx-auto mt-24 p-6 bg-white rounded-xl shadow space-y-4" autoComplete="off">
      <h1 className="font-bold text-xl mb-2">Set a New Password</h1>
      <input
        type="password"
        className="w-full border px-3 py-2 rounded"
        placeholder="New password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
        minLength={8}
        required
      />
      <input
        type="password"
        className="w-full border px-3 py-2 rounded"
        placeholder="Confirm new password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        autoComplete="new-password"
        minLength={8}
        required
      />
      {error && <div className="text-red-600">{error}</div>}
      {success && <div className="text-green-600">{success}</div>}
      <button className="w-full bg-black text-white px-4 py-2 rounded" type="submit" disabled={loading}>
        {loading ? "Updating..." : "Reset Password"}
      </button>
      <a href="/login" className="block mt-4 text-center underline text-blue-600">
        Return to login
      </a>
    </form>
  );
}
